import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import DialogContent from "@mui/material/DialogContent"
import Divider from "@mui/material/Divider"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { styled } from "@mui/material/styles"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import { SUBSCRIPTION_PLANS } from "../../users/billing"
import { selectSubscription } from "../../users/reducer"
import { useDialogContainer } from "../utils"
import CheckoutDrawer from "./CheckoutDrawer"
import DialogHeader from "./DialogHeader"

export default function SelectPlanDrawer({ open, onClose }) {
  const [selectedPlan, setSelectedPlan] = useSelectedPlanState()
  const [checkoutDrawerOpen, setCheckoutDrawerOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useIsMobile()
  const Dialog = useDialogContainer()
  const subscription = useSelector(selectSubscription)

  function handleUpgradeButtonClicked(selectedPlan) {
    setSelectedPlan(selectedPlan)
    setCheckoutDrawerOpen(true)
    onClose()
  }

  function handleSoftClose(planName) {
    onClose()
  }

  return (
    <Dialog
      anchor="right"
      open={open}
      onClose={handleSoftClose}
      fullScreen={isMobile}
      keepMounted
    >
      <DialogHeader title="Choose Your Plan" onClose={handleSoftClose} />
      <DialogContent>
        <Column sx={{ height: "100%" }}>
          <Column sx={{ gap: 3 }}>
            {SUBSCRIPTION_PLANS.map((plan, index) => {
              const planActive = subscription?.isPlanActive(plan) ?? false
              return (
                <PlanContainer
                  key={index}
                  planActive={planActive}
                  planPop={plan.popular}
                >
                  {planActive && (
                    <ActivePlanIndicatorContainer>
                      <CheckCircleIcon
                        sx={{ fontSize: "18px", color: "white" }}
                      />
                      <Typography sx={{ color: "white" }}>
                        Active Plan
                      </Typography>
                    </ActivePlanIndicatorContainer>
                  )}
                  {plan.popular && !planActive && (
                    <PopularPlanBox>Most Popular</PopularPlanBox>
                  )}
                  <Column
                    sx={{ gap: 2.5, mt: plan.popular || planActive ? 1.5 : 0 }}
                  >
                    <Row
                      sx={{
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Row
                          sx={{
                            alignItems: "center",
                            gap: 1.5,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {plan.name}
                          </Typography>
                          {planActive && (
                            <CurrentPlanChip label="Current" size="small" />
                          )}
                        </Row>
                        <Row
                          sx={{
                            alignItems: "center",
                            gap: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Row
                            sx={{
                              alignItems: "baseline",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 700,
                                color: planActive
                                  ? theme.palette.colors.success700
                                  : theme.palette.text.primary,
                              }}
                            >
                              {plan.price.toLocaleString()}
                            </Typography>
                            {plan.period && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.colors.gray600,
                                  fontWeight: 500,
                                }}
                              >
                                /{plan.period}
                              </Typography>
                            )}
                          </Row>
                          {plan.savings ? (
                            <SavingsChip
                              label={`Save $${plan.savings.toLocaleString()}/${
                                plan.period
                              }`}
                              size="small"
                            />
                          ) : null}
                        </Row>
                      </Box>
                    </Row>
                    <Divider
                      sx={{
                        borderColor: planActive
                          ? theme.palette.colors.success200
                          : theme.palette.colors.gray200,
                      }}
                    />
                    <Column sx={{ gap: 1.5 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <Row
                          key={featureIndex}
                          sx={{
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <StyledCheckCircle />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              fontWeight: 500,
                            }}
                          >
                            {feature}
                          </Typography>
                        </Row>
                      ))}
                    </Column>
                    {planActive ? (
                      <ActivePlanRow>
                        <CheckCircleIcon
                          sx={{
                            color: theme.palette.colors.success600,
                            fontSize: "20px",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.colors.success700,
                            fontWeight: 600,
                          }}
                        >
                          You're currently on this plan
                        </Typography>
                      </ActivePlanRow>
                    ) : (
                      <UpgradeButton
                        variant="contained"
                        fullWidth
                        onClick={() => handleUpgradeButtonClicked(plan)}
                        planPop={plan.popular}
                      >
                        Upgrade Now
                      </UpgradeButton>
                    )}
                  </Column>
                </PlanContainer>
              )
            })}
          </Column>
        </Column>
      </DialogContent>
      <CheckoutDrawer
        open={!!checkoutDrawerOpen}
        onClose={() => setCheckoutDrawerOpen(false)}
        initialPlan={selectedPlan}
      />
    </Dialog>
  )
}

const shouldForwardPlanProps = (prop) =>
  ["planActive", "planPop"].indexOf(prop) === -1

const PlanContainer = styled(Box, {
  shouldForwardProp: shouldForwardPlanProps,
})(({ theme, planActive, planPop }) => ({
  border: (theme) =>
    planActive
      ? `3px solid ${theme.palette.colors.success600}`
      : planPop
      ? `2px solid ${theme.palette.colors.brand500}`
      : `2px solid ${theme.palette.colors.gray200}`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: planActive
    ? `linear-gradient(135deg, ${theme.palette.colors.success50}15, ${theme.palette.colors.success50}05)`
    : planPop
    ? theme.palette.colors.brand50
    : "white",
  position: "relative",
  boxShadow: planActive
    ? `0 4px 20px ${theme.palette.colors.success600}20, 0 2px 8px ${theme.palette.colors.success600}10`
    : planPop
    ? `0 4px 16px ${theme.palette.colors.brand400}20`
    : `0 2px 8px ${theme.palette.colors.gray200}40`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: planActive ? "none" : "translateY(-2px)",
    boxShadow: planActive
      ? `0 4px 20px ${theme.palette.colors.success600}20, 0 2px 8px ${theme.palette.colors.success600}10`
      : `0 8px 24px ${theme.palette.colors.gray300}50`,
  },
}))

const ActivePlanIndicatorContainer = styled(Row)(({ theme }) => ({
  alignItems: "center",
  position: "absolute",
  top: -16,
  right: 20,
  gap: theme.spacing(1),
  backgroundColor: theme.palette.colors.success600,
  color: "white",
  padding: theme.spacing(0.75, 2.5),
  borderRadius: theme.spacing(2),
  fontSize: "0.8125rem",
  fontWeight: 700,
  boxShadow: `0 4px 12px ${theme.palette.colors.success600}40`,
  zIndex: 1,
}))

const PopularPlanBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -12,
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: theme.palette.colors.brand500,
  color: "white",
  padding: theme.spacing(0.75, 2.5),
  borderRadius: theme.spacing(2),
  fontSize: "0.8125rem",
  fontWeight: 700,
  boxShadow: `0 4px 12px ${theme.palette.colors.brand400}50`,
  zIndex: 1,
}))

const CurrentPlanChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.colors.success600,
  color: "white",
  fontWeight: 600,
  height: "24px",
  fontSize: "0.6875rem",
  "& .MuiChip-label": {
    paddingInline: theme.spacing(1.5),
  },
}))

const StyledCheckCircle = styled(CheckCircleOutlinedIcon)(({ theme }) => ({
  color: theme.palette.colors.success600,
  fontSize: "20px",
  marginTop: theme.spacing(0.25),
  flexShrink: 0,
}))

const ActivePlanRow = styled(Row)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.colors.success50,
  border: `1.5px solid ${theme.palette.colors.success200}`,
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}))

const UpgradeButton = styled(Button, {
  shouldForwardProp: shouldForwardPlanProps,
})(({ theme, planPop }) => ({
  marginTop: theme.spacing(1),
  paddingBlock: theme.spacing(1.5),
  fontWeight: 600,
  textTransform: "none",
  borderRadius: theme.spacing(1.5),
  fontSize: "0.9375rem",
  background: (theme) =>
    planPop
      ? `linear-gradient(135deg, ${theme.palette.colors.brand500}, ${theme.palette.colors.brand700})`
      : theme.palette.colors.brand500,
  boxShadow: (theme) =>
    planPop
      ? `0 4px 16px ${theme.palette.colors.brand400}50`
      : `0 2px 8px ${theme.palette.colors.brand400}30`,
  "&:hover": {
    background: (theme) =>
      planPop
        ? `linear-gradient(135deg, ${theme.palette.colors.brand600}, ${theme.palette.colors.brand700})`
        : theme.palette.colors.brand600,
    boxShadow: (theme) =>
      planPop
        ? `0 6px 20px ${theme.palette.colors.brand400}60`
        : `0 4px 12px ${theme.palette.colors.brand400}40`,
    transform: "translateY(-1px)",
  },
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}))

const SavingsChip = styled(CurrentPlanChip)(({ theme }) => ({
  fontSize: "0.75rem",
}))

export function useSelectedPlanState() {
  const [selectedPlan, setSelectedPlan] = useState(null)
  const subscription = useSelector(selectSubscription)

  useEffect(
    () => {
      setSelectedPlan(subscription?.isActive ? subscription.plan : null)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscription]
  )

  return [selectedPlan, setSelectedPlan]
}
